import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { useInterval } from 'react-use';
import './ClientHeader.scss';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import ClientInstructions from '../ClientInstructions/ClientInstructions';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import ClientInstructionsVr from '../ClientInstructionsVr/ClientInstructionsVr';
import { hashCurrentSession } from '../../utils/sessionUUIDToCode';
import Key from '../../assets/mainControls/IconKey.svg?react';
import useClientMediaState from '../../hooks/useClientMediaState/useClientMediaState';
import NetworkQualityIndicator from '../UI/NetworkQualityIndicator/NetworkQualityIndicator';
import NetworkReportPopover from '../../helpers/NetworkReport/NetworkReportPopover';
import { runOrReusePreflight } from '../../helpers/NetworkReport/NetworkPreflight';
import { Quality, isBadQuality } from '../../commands/practitioner/enums/CallQuality';
import { MediaStates } from '../../commands/practitioner/enums/MediaStates';
import BackendService from '../../services/BackendService';
import { CLIENT_PAGE_PRESENCE_URI, PRESENCE_HEARTBEAT_MS } from '../../utils/consts';
import AcceptanceBadge from '../AcceptanceBadge/AcceptanceBadge';
type TriggerMode = 'auto' | 'manual';

const qualityTooltipKeyByResult = (quality: Quality): string => {
  switch (quality) {
    case Quality.Excellent:
      return 'quality-test-excellent';
    case Quality.Good:
      return 'quality-test-good';
    case Quality.Fair:
      return 'quality-test-fair';
    case Quality.Poor:
      return 'quality-test-poor';
    case Quality.Error:
    case Quality.Idle:
    case Quality.Testing:
    default:
      return 'quality-test-no-connection';
  }
};

interface ClientHeaderProps {
  clientInstructionsOpened: boolean;
  setClientInstructionsOpened: (value: boolean) => void;
}

const hasValidStats = (r: any) => {
  const s = r?.stats;
  const hasLatency = typeof s?.rtt?.average === 'number';
  const hasJitter = typeof s?.jitter?.average === 'number';
  const hasLoss = typeof s?.packetLoss?.average === 'number';
  return hasLatency && hasJitter && hasLoss;
};

const ClientHeader: React.FC<ClientHeaderProps> = ({ clientInstructionsOpened, setClientInstructionsOpened }) => {
  const [quality, setQuality] = useState<Quality>(Quality.Idle);
  const [report, setReport] = useState<any | null>(null);
  const [openReport, setOpenReport] = useState(false);
  const [lastRunAt, setLastRunAt] = useState<number>(0);
  const qualityAnchorRef = useRef<HTMLSpanElement | null>(null);
  const preflightAbortRef = useRef<AbortController | null>(null);

  const { t } = useTranslation();
  const { hasDevice, sessionId, treatmentId } = useAppContext();
  const hash = hashCurrentSession(sessionId, treatmentId, 'client-hash');
  const { clientMediaURI, isClientPlayerVisible } = useClientMediaState();
  const clientQualityTooltip = `${t('practitioner-call-quality-test')} ${t(qualityTooltipKeyByResult(quality))}`;
  const clientInstructions = useMemo(() => {
    if (!clientInstructionsOpened) {
      return <></>;
    }

    if (hasDevice) {
      return <ClientInstructionsVr setOpen={setClientInstructionsOpened} />;
    }

    return <ClientInstructions setOpen={setClientInstructionsOpened} />;
  }, [clientInstructionsOpened, setClientInstructionsOpened, hasDevice]);

  const hasKickedAuto = useRef(false);
  const presenceHeartbeatCounterRef = useRef(0);

  const sendClientPagePresence = useCallback(
    async (isReady: boolean) => {
      const presenceUri = isReady
        ? `${CLIENT_PAGE_PRESENCE_URI}?hb=${Date.now()}-${++presenceHeartbeatCounterRef.current}`
        : CLIENT_PAGE_PRESENCE_URI;
      try {
        await BackendService.sendMediaStatusReport(
          sessionId,
          treatmentId,
          presenceUri,
          isReady ? MediaStates.Successful : MediaStates.Failure,
          1.0
        );
      } catch (error) {
        console.error('client presence report error:', error);
      }
    },
    [sessionId, treatmentId]
  );

  useEffect(() => {
    if (hasKickedAuto.current) return;
    if (!sessionId || !treatmentId) return;
    hasKickedAuto.current = true;
    testCallConnection('auto');

    return () => {
      preflightAbortRef.current?.abort();
    };
  }, [sessionId, treatmentId]);

  useEffect(() => {
    const onBeforeUnload = () => {
      void sendClientPagePresence(false);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [sendClientPagePresence]);

  const REUSE_MS = 120_000;

  const testCallConnection = async (mode: TriggerMode = 'auto') => {
    if (document.hidden) {
      const onVisible = () => {
        document.removeEventListener('visibilitychange', onVisible);
        testCallConnection(mode);
      };
      document.addEventListener('visibilitychange', onVisible, { once: true });
      return;
    }

    const existing = preflightAbortRef.current;
    if (existing) existing.abort();

    const ac = new AbortController();
    preflightAbortRef.current = ac;

    const isCacheFresh = !!report && Date.now() - lastRunAt < REUSE_MS;
    const willUseCache = mode === 'auto' && isCacheFresh;
    if (!willUseCache) setQuality(Quality.Testing);
    try {
      const result = await runOrReusePreflight({
        sessionId,
        treatmentId,
        getToken: BackendService.getToken,
        durationMs: 10_000,
        recacheMs: REUSE_MS,
        abort: ac.signal,
        cache: mode === 'auto' ? { lastRunAt, report } : undefined,
        tokenKind: 'preflight',
      });

      setReport(result.report);
      setQuality(result.quality);
      setLastRunAt(result.lastRunAt);

      const ok = hasValidStats(result.report);
      setOpenReport(mode === 'manual' ? ok && isBadQuality(result.quality) : ok && result.shouldOpen);
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.error('client preflight error:', e);
        setReport(null);
        setQuality(Quality.Error);
        setLastRunAt(Date.now());
        setOpenReport(true);
      }
    } finally {
      preflightAbortRef.current = null;
    }
  };

  useEffect(() => {
    void sendClientPagePresence(true);
  }, [sendClientPagePresence]);

  useInterval(() => {
    void sendClientPagePresence(true);
  }, PRESENCE_HEARTBEAT_MS);

  return (
    <div className="client-header">
      <div className="title-container">
        <img src={'assets/logo-no-bg-light.png'} alt="psylaris | emdr-remote" />
        <AcceptanceBadge />
      </div>
      <div className="header-right">
        <span ref={qualityAnchorRef} className="quality-report-anchor">
          <NetworkQualityIndicator
            quality={quality}
            title={clientQualityTooltip}
            onClick={() => testCallConnection('manual')}
            size="small"
            disabled={quality === Quality.Testing}
          />
        </span>

        <NetworkReportPopover
          open={openReport && !!report}
          anchorEl={qualityAnchorRef.current}
          quality={quality}
          report={report}
          onClose={() => {
            setOpenReport(false);
            setReport(null);
          }}
        />
        <Tooltip title={t('clientSessionID').toString()} placement="left" PopperProps={{ disablePortal: true }}>
          <div className="client-hash-container">
            <Key className="client-key-icon" />
            {hash}
          </div>
        </Tooltip>
        <LanguageSelector isMedia={!!clientMediaURI && isClientPlayerVisible} size={'large'} />
        <Tooltip title={t('info').toString()} placement="bottom-start" PopperProps={{ disablePortal: true }}>
          <span>
            <button
              className="info-button"
              onClick={(e) => {
                setClientInstructionsOpened(!clientInstructionsOpened);
                e.currentTarget.blur(); // Remove focus from the button after click
              }}
            >
              <svg width="88" height="88" viewBox="0 0 88 88" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M44 88C68.3005 88 88 68.3005 88 44C88 19.6995 68.3005 0 44 0C19.6995 0 0 19.6995 0 44C0 68.3005 19.6995 88 44 88ZM44 80C63.8823 80 80 63.8823 80 44C80 24.1177 63.8823 8 44 8C24.1177 8 8 24.1177 8 44C8 63.8823 24.1177 80 44 80ZM51 25.0133C51 26.6705 50.4306 28.0851 49.2865 29.2481C48.1451 30.417 46.7689 31 45.1606 31C43.5469 31 42.168 30.417 41.0131 29.2481C39.8609 28.0851 39.2834 26.6705 39.2834 25.0133C39.2834 23.3591 39.8609 21.9416 41.0131 20.7637C42.1653 19.5859 43.5469 19 45.1606 19C46.7689 19 48.1451 19.5889 49.2865 20.7637C50.4306 21.9416 51 23.3591 51 25.0133ZM50.4274 64.4664C48.7098 65.2918 47.3361 65.9195 46.3135 66.3523C45.2909 66.785 44.101 67 42.7462 67C40.6657 67 39.047 66.3809 37.8924 65.1485C36.7379 63.9132 36.1606 62.3455 36.1606 60.4453C36.1606 59.7087 36.2031 58.9521 36.2902 58.1811C36.3774 57.4101 36.5164 56.5388 36.7073 55.5672L38.8561 46.3213C39.047 45.4356 39.2096 44.5959 39.3392 43.802C39.4711 43.0138 39.5347 42.2887 39.5347 41.6352C39.5347 40.4544 39.3344 39.629 38.9339 39.1647C38.5333 38.7004 37.7699 38.4653 36.6366 38.4653C36.0805 38.4653 35.5103 38.5743 34.9283 38.7835C34.344 38.9927 34 36.5336 34 36.5336C35.4067 35.8372 36.752 35.241 38.0385 34.7452C39.325 34.2493 40.5408 34 41.6906 34C43.757 34 45.3522 34.6076 46.4714 35.8228C47.5906 37.038 48.1514 38.6144 48.1514 40.5576C48.1514 40.9588 48.1137 41.6668 48.0359 42.6785C47.9581 43.6931 47.8144 44.6217 47.6047 45.4672L45.4653 54.6787C45.2909 55.4182 45.1331 56.2637 44.9964 57.2152C44.855 58.161 44.7867 58.8833 44.7867 59.3676C44.7867 60.5915 45.0105 61.4255 45.4606 61.8697C45.913 62.314 46.6929 62.5347 47.8003 62.5347C48.321 62.5347 48.9124 62.4229 49.5698 62.2022C50.2272 61.9815 50.4274 64.4664 50.4274 64.4664Z"
                />
              </svg>
            </button>
          </span>
        </Tooltip>
      </div>
      {clientInstructions}
    </div>
  );
};

export default ClientHeader;
